<template>
    <div class="page-grid">
        <div class="inputs-holder">
            <div class="inputs-scroll">
                <fieldset>
                    <legend>Project Name</legend>
                    <p-input-text :disabled="!!props.projectInfo.confirmedMatch" v-model="local.projectName" />
                </fieldset>
                <fieldset>
                    <legend>Country Code</legend>
                    <div>
                        Current value: {{ local.countryCode }}. Change to:
                        <primevue-select :options="dataValidationStore.countryCodes" filter v-model="local.countryCode" />
                    </div>
                </fieldset>
                <fieldset>
                    <legend>Power Capacity</legend>
                    <div class="flex-row-with-gap">
                        <p-input-number :model-value="numericPowerCapacity" :max-fraction-digits="5" @update:modelValue="convertNumberValueToStringOnLocal('powerCapacity', $event)" :min="0" />
                        <p-button label="Delete" class="p-button-danger" @click="deleteField('powerCapacity')" />
                    </div>
                </fieldset>
                <fieldset>
                    <legend>Storage Capacity</legend>
                    <div class="flex-row-with-gap">
                        <p-input-number :model-value="numericStorageCapacity" :max-fraction-digits="5" @update:modelValue="convertNumberValueToStringOnLocal('storageCapacity', $event)" :min="0" />
                        <p-button label="Delete" class="p-button-danger" @click="deleteField('storageCapacity')" />
                    </div>
                </fieldset>
                <fieldset>
                    <legend>Location Detail</legend>
                    <template v-if="local.locationNameDetail">
                        <p-button label="Delete" class="p-button-danger" @click="deleteField('locationNameDetail')" />
                        <div>
                            <label>Other Identifiers</label>
                            <div class="flex-row-with-gap">
                                <p-input-text v-model="local.locationNameDetail.otherIdentifiers" />
                            </div>
                        </div>
                        <div>
                            <label>City</label>
                            <div class="flex-row-with-gap">
                                <p-input-text v-model="local.locationNameDetail.city" />
                            </div>
                        </div>
                        <div>
                            <label>Region</label>
                            <div class="flex-row-with-gap">
                                <p-input-text v-model="local.locationNameDetail.region" />
                            </div>
                        </div>
                        <div>
                            <label>Country</label>
                            <div class="flex-row-with-gap">
                                <p-input-text v-model="local.locationNameDetail.country" />
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <p-button label="Add location detail" @click="addLocationDetail" />
                    </template>
                </fieldset>
                <fieldset>
                    <legend>Latitude</legend>
                    <div class="flex-row-with-gap">
                        <p-input-number
                            :model-value="numericLatitude"
                            :max-fraction-digits="10"
                            @update:modelValue="convertNumberValueToStringOnLocal('latitude', $event)"
                            mode="decimal"
                            :max="90"
                            :min="-90"
                            :step="0.000001" />
                        <p-button label="Delete" class="p-button-danger" @click="deleteField('latitude')" />
                    </div>
                </fieldset>
                <fieldset>
                    <legend>Longitude</legend>
                    <div class="flex-row-with-gap">
                        <p-input-number
                            :model-value="numericLongitude"
                            :max-fraction-digits="10"
                            @update:modelValue="convertNumberValueToStringOnLocal('longitude', $event)"
                            mode="decimal"
                            :max="180"
                            :min="-180"
                            :step="0.000001" />
                        <p-button label="Delete" class="p-button-danger" @click="deleteField('longitude')" />
                    </div>
                </fieldset>
                <fieldset>
                    <legend>Segment</legend>
                    <div class="flex-row-with-gap">
                        Current value: {{ local.segment }}. Change to:
                        <primevue-select :options="dataValidationStore.segments" filter v-model="local.segment" />
                        <p-button label="Delete" class="p-button-danger" @click="deleteField('segment')" />
                    </div>
                </fieldset>
                <fieldset>
                    <legend>Installation</legend>
                    <div class="flex-row-with-gap">
                        Current value: {{ local.installation }}. Change to:
                        <primevue-select :options="dataValidationStore.installations" filter v-model="local.installation" />
                        <p-button label="Delete" class="p-button-danger" @click="deleteField('installation')" />
                    </div>
                </fieldset>
                <fieldset>
                    <legend>Technology</legend>
                    <div class="flex-row-with-gap">
                        Current value: {{ local.technology }}. Change to:
                        <primevue-select :options="dataValidationStore.technologies" filter v-model="local.technology" />
                        <p-button label="Delete" class="p-button-danger" @click="deleteField('technology')" />
                    </div>
                </fieldset>
                <fieldset>
                    <legend>Status Array (JSON)</legend>
                    Add a new status:
                    <div class="flex-row-with-gap">
                        <div>
                            <primevue-select placeholder="Select a status" :options="dataValidationStore.statuses" filter v-model="statusToAdd" />
                        </div>
                        <div>
                            Date: <basic-date-picker v-model="dateForStatusToAdd" />
                        </div>
                        <p-button
                            label="Add status"
                            @click="addStatus(statusToAdd, dateForStatusToAdd as Date)"
                            :disabled="!statusToAdd || !dateForStatusToAdd" />
                    </div>
                    <div v-for="(status, index) in local.statusArray" :key="index">
                        <status-editor
                            :status-date="status"
                            @change-status="(updatedStatus) => changeStatus(index, updatedStatus)"
                            @delete="deleteStatusByIndex(index)" />
                    </div>
                </fieldset>
                <company-list-editor company-type="Financer" v-model="local.financer" />
                <company-list-editor company-type="Tech provider" v-model="local.technologyProvider" />
                <company-list-editor company-type="Developer" v-model="local.developer" />
                <company-list-editor company-type="Owner" v-model="local.owner" />
                <company-list-editor company-type="Trader" v-model="local.trader" />
                <company-list-editor company-type="Operator" v-model="local.operator" />
            </div>
            <div class="flex-row-with-gap">
                <p-button @click="save">Save</p-button>
                <p-button type="button" @click="cancel">Cancel</p-button>
            </div>
        </div>
        <div v-if="linkedProjectData">
            <project-panel-redacted-data :selected-project="linkedProjectData" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import BasicDatePicker from '@/components/powerMarkets/common/BasicDatePicker.vue';
import PInputText from 'primevue/inputtext';
import PInputNumber from 'primevue/inputnumber';
import PButton from 'primevue/button';
import PrimevueSelect from 'primevue/select';
import { useToast } from 'primevue/usetoast';
import { IProjectInfo, IStatusDate } from '@/interfaces/storageTracking/IArticle.ts';
import { UseDataValidationStore } from '@/stores/storageTracking/DataValidationStore.ts';
import { useAllDataStore } from '@/stores/storageTracking/AllDataStore.ts';
import { IRedactedProjectData } from '@/interfaces/storageTracking/IRedactedProjectData.ts';
import { cloneDeep } from 'lodash';
import ProjectPanelRedactedData from '../projectSearch/ProjectPanelRedactedData.vue';
import CompanyListEditor from './CompanyListEditor.vue';
import StatusEditor from './StatusEditor.vue';

const dataValidationStore = UseDataValidationStore();
const allDataStore = useAllDataStore();
const toast = useToast();

const props = defineProps<{
    projectInfo: IProjectInfo;
}>();
const emit = defineEmits<{(e: 'update', value: IProjectInfo): void;
    (e: 'cancel'): void;
}>();

const statusToAdd = ref<string | null>(null);
const dateForStatusToAdd = ref<Date | null>(null);

const linkedProjectData = computed<IRedactedProjectData | null>(() => {
    if (!props.projectInfo.confirmedMatch) return null;
    return getMatchingProject(props.projectInfo.confirmedMatch);
});

const local = ref<IProjectInfo>(cloneDeep(props.projectInfo));

function toNumber (s?: string | null): number | null {
    if (s === undefined || s === null) return null;
    const n = parseFloat(s);
    return Number.isNaN(n) ? null : n;
}

function deleteStatusByIndex (index: number): void {
    if (!local.value.statusArray) return;
    if (index < 0 || index >= local.value.statusArray.length) return;
    local.value.statusArray.splice(index, 1);
}

function getMatchingProject (id: string): IRedactedProjectData | null {
    return allDataStore.allRedactedDataUnfiltered.filteredProjects.find(item => item.factStorageId?.toString() === id) ?? null;
}

const numericLatitude = ref<number | null>(toNumber(local.value.latitude));
const numericLongitude = ref<number | null>(toNumber(local.value.longitude));
const numericPowerCapacity = ref<number | null>(toNumber(local.value.powerCapacity));
const numericStorageCapacity = ref<number | null>(toNumber(local.value.storageCapacity));

function addLocationDetail (): void {
    local.value.locationNameDetail = {
        otherIdentifiers: '',
        city: '',
        region: '',
        country: '',
    };
}

function save () : void {
    if (!local.value.projectName || local.value.projectName.trim() === '') {
        toast.add({ severity: 'error', life: 10000, summary: 'Please add a project name first' });
        return;
    }
    if (!local.value.countryCode || local.value.countryCode.trim() === '') {
        toast.add({ severity: 'error', life: 10000, summary: 'Please add a country code first' });
        return;
    }
    emit('update', local.value);
}

function cancel (): void {
    emit('cancel');
}

function deleteField (field: keyof IProjectInfo): void {
    local.value[field] = undefined;
    switch (field) {
        case 'latitude': numericLatitude.value = null; break;
        case 'longitude': numericLongitude.value = null; break;
        case 'powerCapacity': numericPowerCapacity.value = null; break;
        case 'storageCapacity': numericStorageCapacity.value = null; break;
        default: break;
    }
}

function addStatus (statusToAddLocal: string | null, date: Date | null): void {
    if (!statusToAddLocal || !date) return;
    if (!local.value.statusArray) {
        local.value.statusArray = [];
    }
    const dateAsString = new Intl.DateTimeFormat('en-GB').format(date);
    local.value.statusArray.push({
        status: statusToAddLocal,
        date: dateAsString,
    });
    statusToAdd.value = null;
    dateForStatusToAdd.value = null;
}

function changeStatus (index: number, statusDate: IStatusDate): void {
    if (!statusDate.status || !statusDate.date) return;
    if (!local.value.statusArray || index < 0 || index >= local.value.statusArray.length) return;
    local.value.statusArray[index] = statusDate;
}

function convertNumberValueToStringOnLocal (field: 'latitude' | 'longitude' | 'powerCapacity' | 'storageCapacity', numericValue: number | null): void {
    local.value[field] = String(numericValue) ?? undefined;
}

</script>

<style lang="scss" scoped>
    .page-grid {
        display:flex;
        gap:var(--defaultSpacing);
        overflow:hidden;
        height: 100%;
        min-height: 0;
        padding: var(--defaultSpacing);
        .inputs-holder {
            display: grid;
            grid-template-rows: 1fr auto;
            gap: var(--defaultSpacing);
            overflow-y: hidden;
            .inputs-scroll {
                overflow-y: auto;
                min-height: 0;
            }
        }
    }
</style>
